// CompanyLogo — square avatar tile for a company.
//
// 1. If we have a usable domain, render the Clearbit free-logo CDN URL.
//    No API key required, no signup, works for most public brands.
// 2. If Clearbit 404s (or the company has no domain at all), fall through
//    to coloured initials — first two letters of the name on a stable
//    per-name colour so the tile looks consistent across pages.
//
// Usage:
//   <CompanyLogo company={{ name, website, linkedin_url }} size={56} />
//
// Caller can pass `size` (px) for the box edge; defaults to 40.

import React, { useState, useEffect } from 'react';

// Pull a bare domain out of a website-ish field. Handles bare strings
// ("acme.com"), full URLs ("https://www.acme.com/path"), and gracefully
// returns null when nothing useful is in there.
function domainFromWebsite(s) {
  if (!s) return null;
  let v = String(s).trim().toLowerCase();
  if (!v) return null;
  v = v.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const slash = v.indexOf('/');
  if (slash >= 0) v = v.slice(0, slash);
  // Reject anything that doesn't look like a domain (e.g. linkedin slugs).
  if (!v.includes('.')) return null;
  return v;
}

// Stable hue per name so the same company always gets the same tile colour.
function hueFromName(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function CompanyLogo({ company, size = 40, className = '' }) {
  const name = (company && company.name) || '';
  const domain = domainFromWebsite(company && (company.website || company.domain));
  const src = domain ? `https://logo.clearbit.com/${domain}` : null;
  const [imgOk, setImgOk] = useState(!!src);

  // Reset the loaded flag when the domain changes — otherwise switching
  // between companies in a list keeps the previous one's broken state.
  useEffect(() => { setImgOk(!!src); }, [src]);

  const hue = hueFromName(name);
  const bg = `hsl(${hue}, 55%, 22%)`;
  const fg = `hsl(${hue}, 80%, 75%)`;

  const style = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.18),
    flexShrink: 0,
    background: bg,
    color: fg,
    fontSize: Math.round(size * 0.36),
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    letterSpacing: '0.5px',
  };

  if (src && imgOk) {
    return (
      <div className={className} style={style}>
        <img
          src={src}
          alt={name}
          onError={() => setImgOk(false)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div className={className} style={style} aria-label={name}>
      {initials(name)}
    </div>
  );
}
